import { CellContext } from '@tanstack/react-table';
import styled from 'styled-components';
import { useMemo } from 'react';
import { truncate } from 'lodash';
import { PositionView } from '@paradigm/features/src/positions/entities/positions';
import PopoverCell from '#/components/UnifiedRfqs/components/PopoverCell';
import {
  hasSameAccount,
  hasSameProduct,
} from '@paradigm/features/src/positions/contexts/position-groups';
import { WithFormattingProps } from '@paradigm/features/src/unified-rfqs/ui/types';
import { CreateMarketButtons } from '../PositionsMarketCreation';
import { getGroupPositions } from './utils';

interface CellProps {
  info: CellContext<PositionView, PositionView>;
  placeholder?: string;
  dataTestid?: string;
}

export function TextCell({ info, dataTestid, placeholder = '' }: CellProps) {
  const value = info.getValue<string | string[]>();
  //INFO When aggregating value can be string[] then hide it if there is more than one value
  if (Array.isArray(value) && value.length > 1)
    return <Text data-testid={dataTestid}>{placeholder}</Text>;
  return <Text data-testid={dataTestid}>{value}</Text>;
}

const ACCOUNT_MAX_LENGTH = 17;
const ACCOUNT_SLICE_LENGTH = 18;

const getAccount = (value: string | string[]): string => {
  if (Array.isArray(value))
    return value.length > 0 && value[0] != null ? value[0] : '';
  return value;
};

export function AccountCell({ info }: CellProps) {
  const value = info.getValue<string | string[]>();
  const displayValue = getAccount(value);

  const shouldTruncateAccount = displayValue.length > ACCOUNT_MAX_LENGTH;
  const accountDisplay = useMemo(() => {
    if (shouldTruncateAccount) {
      return truncate(displayValue, { length: ACCOUNT_SLICE_LENGTH });
    }

    return displayValue;
  }, [displayValue, shouldTruncateAccount]);

  // Hide if there is more than 1 account in group
  const isNotUnique = Array.isArray(value) && value.length > 1;
  if (isNotUnique) {
    return null;
  }

  return (
    <PopoverCell
      id={`position-account-${info.row.original.id}`}
      popoverValue={value}
      shouldDisplayPopover={shouldTruncateAccount}
    >
      <Text data-testid="account">{accountDisplay}</Text>
    </PopoverCell>
  );
}

export function ActionsCell({ info }: CellProps) {
  if (!info.row.getIsGrouped()) {
    return (
      <Actions>
        <CreateMarketButtons
          baseDDTag="Position Row"
          positions={[info.row.original]}
        />
      </Actions>
    );
  }

  const groupPositions = getGroupPositions(info.row);
  if (hasSameAccount(groupPositions) && hasSameProduct(groupPositions)) {
    return (
      <Actions>
        <CreateMarketButtons
          baseDDTag="Position Grouped"
          positions={groupPositions}
        />
      </Actions>
    );
  }

  return null;
}

export function FormattedCell<T>({ info, dataTestid }: CellProps) {
  const { formatted } = info.getValue<WithFormattingProps<T>>();
  return <Text data-testid={dataTestid}>{formatted}</Text>;
}

export function TextColored({ info }: CellProps) {
  const value = info.getValue<PositionView['unrealizedPnl']>();
  return <Text color={value.color}>{value.formatted}</Text>;
}

const Text = styled.div<{ color?: string }>`
  color: ${({ color }) => (color != null ? color : '#ffffff')};
  opacity: ${({ color }) => (color != null ? 1 : 0.6)};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;
